import { useId, useState, type FocusEvent, type InputHTMLAttributes } from 'react';
import { cx } from './utils';

type NumberStepperValue = number | null;

export interface NumberStepperProps<Value extends NumberStepperValue = number> extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'defaultValue' | 'onChange' | 'step'> {
  value?: Value;
  defaultValue?: Value;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  allowEmpty?: boolean;
  decrementLabel: string;
  incrementLabel: string;
  onValueChange?: (value: Value) => void;
}

function clampValue(value: NumberStepperValue, min?: number, max?: number): NumberStepperValue {
  if (value === null || !Number.isFinite(value)) return null;
  if (typeof min === 'number' && value < min) return min;
  if (typeof max === 'number' && value > max) return max;
  return value;
}

function normalizeDraftValue(value: string) {
  return value
    .replace(/^(-?)0+(?=\d)/, '$1')
    .replace(/^(-?)\.(?=\d)/, '$10.');
}

export function NumberStepper<Value extends NumberStepperValue = number>({
  value,
  defaultValue,
  min,
  max,
  step = 1,
  label,
  allowEmpty = false,
  decrementLabel,
  incrementLabel,
  onValueChange,
  className,
  id,
  disabled,
  onBlur,
  onFocus,
  ...props
}: NumberStepperProps<Value>) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const isControlled = value !== undefined;
  const initialValue = defaultValue === undefined ? (allowEmpty ? null : 0) : defaultValue;
  const [internalValue, setInternalValue] = useState(() => clampValue(initialValue, min, max));
  const [draftValue, setDraftValue] = useState<string | null>(null);
  const currentValue = isControlled ? clampValue(value, min, max) : internalValue;

  const setNextValue = (nextValue: number) => {
    const clampedValue = clampValue(nextValue, min, max);
    if (!isControlled) {
      setInternalValue(clampedValue);
    }
    onValueChange?.(clampedValue as Value);
  };

  const setEmptyValue = () => {
    if (!allowEmpty) return;
    if (!isControlled) {
      setInternalValue(null);
    }
    onValueChange?.(null as Value);
  };

  const commitDraftValue = (rawValue: string) => {
    const normalizedValue = normalizeDraftValue(rawValue);
    if (normalizedValue === '') {
      if (allowEmpty) {
        setEmptyValue();
      } else {
        setNextValue(emptyBaseValue);
      }
      setDraftValue(null);
      return;
    }

    const nextValue = Number(normalizedValue);
    if (Number.isFinite(nextValue)) {
      setNextValue(nextValue);
    } else if (allowEmpty) {
      setEmptyValue();
    } else {
      setNextValue(emptyBaseValue);
    }
    setDraftValue(null);
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setDraftValue(currentValue === null ? '' : String(currentValue));
    onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    commitDraftValue(draftValue ?? event.currentTarget.value);
    onBlur?.(event);
  };

  const canDecrement = !disabled && currentValue !== null && (typeof min !== 'number' || currentValue > min);
  const canIncrement = !disabled && (currentValue === null || typeof max !== 'number' || currentValue < max);
  const emptyBaseValue = typeof min === 'number' ? min : 0;
  const displayValue = draftValue ?? (currentValue === null ? '' : String(currentValue));

  return (
    <div className={cx('ui-number-stepper', className)}>
      {label ? <label htmlFor={inputId} className="ui-number-stepper__label">{label}</label> : null}
      <div className="ui-number-stepper__control">
        <button
          type="button"
          className="ui-number-stepper__button"
          aria-label={decrementLabel}
          disabled={!canDecrement}
          onClick={() => currentValue !== null && setNextValue(currentValue - step)}
        >
          -
        </button>
        <input
          {...props}
          id={inputId}
          type="number"
          className="ui-number-stepper__input"
          disabled={disabled}
          max={max}
          min={min}
          step={step}
          value={displayValue}
          onBlur={handleBlur}
          onChange={(event) => {
            const normalizedValue = normalizeDraftValue(event.currentTarget.value);
            setDraftValue(normalizedValue);
            if (normalizedValue === '') {
              if (allowEmpty) {
                setEmptyValue();
              }
              return;
            }
            const nextValue = Number(normalizedValue);
            if (Number.isFinite(nextValue)) {
              setNextValue(nextValue);
            }
          }}
          onFocus={handleFocus}
        />
        <button
          type="button"
          className="ui-number-stepper__button"
          aria-label={incrementLabel}
          disabled={!canIncrement}
          onClick={() => setNextValue((currentValue ?? emptyBaseValue) + step)}
        >
          +
        </button>
      </div>
    </div>
  );
}
